import { useApp } from "@/context/AppContext";
import { X, Plus, Minus, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";

export const CartDrawer = () => {
  const { cartOpen, setCartOpen, cart, updateQty, removeFromCart, subtotal } = useApp();

  return (
    <>
      <div
        className={`fixed inset-0 z-50 transition-opacity duration-300 ${
          cartOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        style={{ background: "rgba(43, 30, 22, 0.45)" }}
        onClick={() => setCartOpen(false)}
        data-testid="cart-overlay"
      />
      <aside
        className={`fixed right-0 top-0 h-full w-full sm:w-[440px] z-50 transition-transform duration-400 ease-out ${
          cartOpen ? "translate-x-0" : "translate-x-full"
        }`}
        style={{ background: "var(--bg)" }}
        data-testid="cart-drawer"
      >
        <div className="flex items-center justify-between px-6 py-5 border-b" style={{ borderColor: "var(--line)" }}>
          <h2 className="font-display text-2xl">Your Basket</h2>
          <button onClick={() => setCartOpen(false)} aria-label="Close" data-testid="close-cart-btn">
            <X size={20} strokeWidth={1.5} />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-4" style={{ height: "calc(100vh - 220px)" }}>
          {cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center gap-4 py-20">
              <p className="font-display text-xl" style={{ color: "var(--ink-soft)" }}>Your basket is empty.</p>
              <Link to="/shop" onClick={() => setCartOpen(false)} className="btn-secondary" data-testid="empty-cart-shop-btn">
                Browse the bakery
              </Link>
            </div>
          ) : (
            <ul className="space-y-6">
              {cart.map((item) => (
                <li key={item.cart_key} className="flex gap-4" data-testid={`cart-item-${item.cart_key}`}>
                  <div className="w-20 h-24 overflow-hidden rounded-sm flex-shrink-0" style={{ background: "var(--bg-soft)" }}>
                    {item.image && <img src={item.image} alt={item.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 flex flex-col">
                    <div className="flex justify-between gap-2">
                      <h4 className="font-display text-lg leading-tight">{item.name}</h4>
                      <button onClick={() => removeFromCart(item.cart_key)} data-testid={`remove-${item.cart_key}`}>
                        <Trash2 size={16} strokeWidth={1.5} />
                      </button>
                    </div>
                    <p className="text-sm mt-1" style={{ color: "var(--ink-soft)" }}>₹{Math.round(item.price)}</p>
                    {item.bundle_selection && item.bundle_selection.length > 0 && (
                      <p className="text-xs mt-1 leading-snug" style={{ color: "var(--ink-soft)" }} data-testid={`bundle-list-${item.cart_key}`}>
                        {(() => {
                          const c = item.bundle_selection.reduce((m, n) => ({ ...m, [n]: (m[n] || 0) + 1 }), {});
                          return Object.entries(c).map(([n, q]) => (q > 1 ? `${q}× ${n}` : n)).join(", ");
                        })()}
                      </p>
                    )}
                    <div className="flex items-center gap-3 mt-auto">
                      <button
                        onClick={() => updateQty(item.cart_key, item.quantity - 1)}
                        className="border w-7 h-7 flex items-center justify-center"
                        style={{ borderColor: "var(--line)" }}
                        data-testid={`decrease-${item.cart_key}`}
                      >
                        <Minus size={12} strokeWidth={1.5} />
                      </button>
                      <span className="text-sm" data-testid={`qty-${item.cart_key}`}>{item.quantity}</span>
                      <button
                        onClick={() => updateQty(item.cart_key, item.quantity + 1)}
                        className="border w-7 h-7 flex items-center justify-center"
                        style={{ borderColor: "var(--line)" }}
                        data-testid={`increase-${item.cart_key}`}
                      >
                        <Plus size={12} strokeWidth={1.5} />
                      </button>
                      <span className="ml-auto text-sm font-medium">
                        ₹{Math.round(item.price * item.quantity)}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {cart.length > 0 && (
          <div className="absolute bottom-0 left-0 right-0 px-6 py-5 border-t" style={{ borderColor: "var(--line)", background: "var(--bg)" }}>
            <div className="flex justify-between mb-4">
              <span className="eyebrow">Subtotal</span>
              <span className="font-display text-xl" data-testid="cart-subtotal">₹{Math.round(subtotal)}</span>
            </div>
            <Link
              to="/checkout"
              onClick={() => setCartOpen(false)}
              className="btn-primary w-full justify-center"
              data-testid="checkout-btn"
            >
              Proceed to Checkout
            </Link>
            <p className="text-xs text-center mt-3" style={{ color: "var(--ink-soft)" }}>
              Free local delivery on orders over ₹500
            </p>
          </div>
        )}
      </aside>
    </>
  );
};
